import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isCommittedSaveError, ticketRecoveryReason, ticketKeys } from '@field-tickets/shared';
import type { UpdateTicketData } from '@field-tickets/shared';
import {
  getAllQueuedTicketSaves,
  ticketSaveEntryId,
  type QueuedTicketSave,
  replayAllQueuedTicketSaves,
  type TicketConflictSnapshot,
  type TicketSaveLease,
} from '@/utils/offlineQueue';
import { getAuthEpoch, isCurrentAuthEpoch } from '@/utils/authEpoch';
import { sendTicketPut } from '@/utils/ticketPut';
import { showToast } from '@/components/Toast';
import {
  anyTicketRecoveryDraft,
  recordTicketRecovery,
  ticketRecoveryOwner,
} from '@/utils/ticketRecovery';
import { useAuthStore } from '@/store/auth';

/**
 * Bounded backoff ladder (ms) for self-scheduled replay retries. When a drain
 * leaves items still queued — a gateway bounce kept them — the hook retries
 * itself on this ladder so the save lands with NO user action. It escalates
 * then STOPS: a backend that is down for a non-transient reason shouldn't be
 * hammered forever, and a later focus / visibility / successful-request trigger
 * still kicks a fresh pass. A drain that empties the queue resets the ladder.
 */
const REPLAY_BACKOFF_MS = [3000, 8000, 20000];

/**
 * Drains the offline ticket-save queue and replays queued PUTs when the backend
 * is reachable again. A ticket form ENQUEUES on an offline/gateway save failure
 * (offline, fetch TypeError, status-0 timeout, or a 502/503/504 deploy bounce)
 * and this hook flushes the queue.
 *
 * Replay triggers — deliberately broad, because the classic failure is a
 * zero-downtime DEPLOY: the backend bounces for a few seconds but the browser
 * STAYS online, so the window 'online' event never fires. Relying on 'online'
 * alone would leave a queued save stranded indefinitely. So we also drain on:
 *  - app load (mount),
 *  - tab refocus ('focus') and re-show ('visibilitychange' → visible) — the tech
 *    backgrounds the form during the bounce and comes back,
 *  - a bounded backoff timer while anything is still queued (no user action), and
 *  - the next successful request (React Query MutationCache/QueryCache success) —
 *    the first save/fetch that lands is the signal the backend recovered.
 *
 * The form and this replay CAN both target the same ticketId (e.g. the tech
 * keeps editing during the reconnect round-trip), so they share the per-ticket
 * lock in offlineQueue (`withTicketSaveLock`). That lock guarantees a replay PUT
 * and a form autosave never overlap, and `replayQueuedTicketSave` re-reads the
 * queue under the lock so a form save that landed first (and cleared the entry)
 * makes the replay a no-op instead of reverting the row to a stale snapshot.
 * Every trigger funnels through one re-entrancy-guarded `drain`, so the many
 * triggers can never stack into overlapping passes. On success it invalidates
 * the ticket cache (so any open view re-hydrates from the server), and
 * offlineQueue fires OFFLINE_SAVE_REPLAYED_EVENT so a mounted form flips its
 * pill back to "saved".
 */
export function useOfflineQueueReplay(): void {
  const queryClient = useQueryClient();
  const owner = useAuthStore((state) => ticketRecoveryOwner(state.user));

  useEffect(() => {
    if (!owner) return;
    let cancelled = false;
    // Single-flight guard: focus + visibilitychange + a cache success can all
    // fire at once. The per-ticket lock already makes individual PUTs safe; this
    // collapses the triggers into ONE pass and re-runs once if more arrived
    // mid-drain, so we never stack redundant IndexedDB scans / replay passes.
    let draining = false;
    let drainQueued = false;
    let backoffTimer: ReturnType<typeof setTimeout> | undefined;
    let backoffStep = 0;

    // Every replay PUT goes through the gate (#967): under the ticket's save
    // lock, never while a recovery draft of the ticket exists, and dropped
    // (StaleSessionError) when the session changed since the drain started.
    const putOnce = async (
      payload: UpdateTicketData,
      lease: TicketSaveLease,
      epoch: number,
    ) => {
      try {
        await sendTicketPut(lease, payload, { epoch, owner });
      } catch (error) {
        // The replay committed; only the server's answer failed (500
        // saved_reload_required, or a partial_save whose ticket row was
        // written). Done: the cache invalidation below re-reads the ticket.
        if (isCommittedSaveError(error)) return;
        throw error;
      }
    };

    // A replay that ends in recovery (uncertain, or a revision conflict) enters the same recovery as an open form: the durable draft,
    // which no replay sends; an open form of this ticket hears it and goes
    // read-only. The queue entry is still marked failed (blocked) below.
    const makePut = (epoch: number) => async (
      ticketId: string,
      payload: UpdateTicketData,
      baseSnapshot: TicketConflictSnapshot | undefined,
      lease: TicketSaveLease,
      entry?: QueuedTicketSave,
    ) => {
      try {
        await putOnce(payload, lease, epoch);
      } catch (error) {
        const reason = ticketRecoveryReason(error);
        if (reason && reason !== 'committed' && isCurrentAuthEpoch(epoch)) {
          recordTicketRecovery(
            owner,
            ticketId,
            reason,
            payload,
            baseSnapshot,
            entry && ticketSaveEntryId(entry),
          );
        }
        throw error;
      }
    };

    const clearBackoff = () => {
      if (backoffTimer !== undefined) {
        clearTimeout(backoffTimer);
        backoffTimer = undefined;
      }
    };

    const drain = async (): Promise<void> => {
      if (cancelled) return;
      // Don't attempt while the browser KNOWS it's offline — every PUT would
      // fail and the queue would be kept anyway. We retry on the next trigger.
      // (A deploy bounce does NOT set navigator.onLine=false, so this guard
      // doesn't block the deploy-recovery case the gateway-code handling targets.)
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (draining) {
        drainQueued = true;
        return;
      }
      draining = true;
      try {
        do {
          drainQueued = false;
          const epoch = getAuthEpoch();
          const { replayed, failed } = await replayAllQueuedTicketSaves(owner, makePut(epoch), {
            // This owner's recovery drafts hold their saves across tabs.
            isBlocked: (ticketId) => anyTicketRecoveryDraft(ticketId, owner),
          });
          if (cancelled || !isCurrentAuthEpoch(epoch)) return;
          replayed.forEach((ticketId) => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
          });
          if (failed.length > 0) {
            showToast(
              'A queued ticket edit was rejected. Open the ticket to review and retry it.',
              'error',
            );
          }
        } while (drainQueued && !cancelled);
      } finally {
        draining = false;
      }
      if (cancelled) return;

      // If anything is still queued, the pass stopped on a kept-offline PUT (the
      // backend is still bouncing). Schedule a bounded backoff retry so it
      // replays without the tech doing anything. An empty queue resets the ladder.
      let stillQueued = false;
      try {
        // Reached-server failures stay recoverable but are blocked until the
        // user edits/retries them; only retry entries that are still offline.
        stillQueued = (await getAllQueuedTicketSaves(owner)).some((entry) => !entry.failure);
      } catch {
        stillQueued = false;
      }
      if (cancelled) return;
      if (stillQueued && backoffStep < REPLAY_BACKOFF_MS.length) {
        const delay = REPLAY_BACKOFF_MS[backoffStep];
        backoffStep += 1;
        clearBackoff();
        backoffTimer = setTimeout(() => {
          backoffTimer = undefined;
          void drain();
        }, delay);
      } else if (!stillQueued) {
        backoffStep = 0;
        clearBackoff();
      }
    };

    const triggerDrain = () => {
      void drain();
    };

    void drain();

    const handleOnline = () => triggerDrain();
    const handleFocus = () => triggerDrain();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') triggerDrain();
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    // Replay on the next successful request: when the backend recovers, the
    // first mutation/query that succeeds is the signal that PUTs will land
    // again. Replay PUTs run via the raw API (not React Query), so they don't
    // re-enter these caches — no feedback loop. The single-flight guard +
    // per-ticket lock keep it idempotent against an in-flight form save.
    const onCacheEvent = (event: unknown) => {
      const e = event as { type?: string; action?: { type?: string } } | undefined;
      if (e?.type === 'updated' && e.action?.type === 'success') {
        triggerDrain();
      }
    };
    const unsubscribeMutations = queryClient.getMutationCache().subscribe(onCacheEvent);
    const unsubscribeQueries = queryClient.getQueryCache().subscribe(onCacheEvent);

    return () => {
      cancelled = true;
      clearBackoff();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubscribeMutations();
      unsubscribeQueries();
    };
  }, [owner, queryClient]);
}
